import type { TextRange } from "@nexxt/ui";
import { codeKeyOf, foldText } from "./fold";

// Which parts of a found text answer the query, compared as the domain compared them (ADR-117 point 4): a text
// without case and accents, a code by its letters and digits, so `po0001` marks `PO-0001` whole. A word found only
// as a typo marks nothing.

/** Each character of `text` that counts, folded as `fold` does, with where it starts and ends in `text`. */
function foldedWithIndex(text: string, fold: (c: string) => string) {
  let folded = "";
  const starts: number[] = [];
  const ends: number[] = [];
  let i = 0;
  for (const char of text) {
    const f = fold(char);
    folded += f;
    // one entry per UTF-16 unit, as `indexOf` counts in `folded`: a character outside the BMP is two
    for (let unit = 0; unit < f.length; unit++) {
      starts.push(i);
      ends.push(i + char.length);
    }
    i += char.length;
  }
  return { folded, starts, ends };
}

/** As a text first; a text that answers nothing that way is tried as a code (a PO number, a SKU code). */
export function highlightRanges(text: string, q: string): TextRange[] {
  const asText = rangesOf(text, q, foldText);
  return asText.length > 0 ? asText : rangesOf(text, q, codeKeyOf);
}

function rangesOf(text: string, q: string, fold: (text: string) => string): TextRange[] {
  const { folded, starts, ends } = foldedWithIndex(text, fold);
  const ranges: [number, number][] = [];
  for (const word of q.split(/\s+/)) {
    const term = fold(word);
    if (term === "") continue;
    for (let from = folded.indexOf(term); from >= 0; from = folded.indexOf(term, from + term.length)) {
      const start = starts[from] ?? 0;
      ranges.push([start, ends[from + term.length - 1] ?? start]);
    }
  }
  return merge(ranges);
}

function merge(ranges: [number, number][]): TextRange[] {
  const sorted = ranges.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const [start, end] of sorted) {
    const prev = merged.at(-1);
    if (prev && start <= prev[1]) prev[1] = Math.max(prev[1], end);
    else merged.push([start, end]);
  }
  return merged;
}
